import { inject, Inject, Injectable } from '@angular/core';
import { LOCAL_STORAGE } from 'libs/storage/src/lib/tokens/local-storage.token';
import { StorageConfig } from '../models/config.model';
import { STORAGE_CONFIG } from '../tokens/config.token';

import { CryptoStorageService } from './crypto-storage.service';

interface ExtendedValue<T = string> {
  value: T;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService implements Storage {

  readonly length = 0;
  private _defaultStorage: Storage;

  constructor(
    cryptoStorage: CryptoStorageService,
    @Inject(STORAGE_CONFIG) config: StorageConfig,
  ) {
    this._defaultStorage = config.isTest ? inject(LOCAL_STORAGE) : cryptoStorage;
  }

  /** Проверяет ключ в хранилище на истечение срока и удаляет, если ключ истек, или возвращает значение ключа
   * */
  public getItem<T = string>(key: string, storage: Storage = this._defaultStorage): T | null {
    const item = JSON.parse(storage.getItem(key) || '{}');

    const isExpired = item?.timestamp == 0 ? false : item?.timestamp < (+new Date() / 1000);
    if (isExpired) {
      storage.removeItem(key);
      return null;
    } else {
      return <T>item?.value || null;
    }
  }

  /** Установка значения в хранилище
   * @param key
   * @param value
   * @param expiredTimeInDays при значении 0 ключ никогда не устаревает
   * @param storage при значении sessionStorage {expiredTimeInDays} не важно
   * */
  public setItem<T = string>(
    key: string,
    value: T,
    expiredTimeInDays = 0,
    storage: Storage = this._defaultStorage,
  ): void {
    const today = new Date();
    const _value: ExtendedValue<T> = {
      value,
      timestamp: expiredTimeInDays != 0 ? today.setDate(today.getDate() + expiredTimeInDays) / 1000 : 0,
    };

    storage.setItem(key, JSON.stringify(_value));
  }

  public removeItem(key: string, storage: Storage = this._defaultStorage): void {
    storage.removeItem(key);
  }

  public key(index: number, storage: Storage = this._defaultStorage): string | null {
    return Object.keys(storage).find((k, i) => i === index) || null;
  }

  public clear(storage: Storage = this._defaultStorage): void {
    storage.clear();
  }

  public check(key: string, storage: Storage = this._defaultStorage): boolean {
    return storage.getItem(key) !== null;
  }
}
